import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../../AppStores";
import {TextInputV5} from "../../details/datatypes/TextInputV5";
import {doesStructureContainElement, type IViewFolder} from "../../../../data/models/ViewUtils";
import type {IDropdownOption} from "../../interaction/DropdownOptionsV5";
import {ConfirmWindowV5} from "../../popup/ConfirmWindowV5";
import OpenFolderIcon from "../../icons/folder-open.svg?react";
import CloseFolderIcon from "../../icons/folder-close.svg?react";
import DotsHorizontalIcon from "../../icons/dots-horizontal.svg?react";
import PlusIcon from "../../icons/circle-plus.svg?react";
import {DropdownButtonV5} from "../../interaction/DropdownButtonV5";
import {IconButtonV5} from "../../interaction/IconButtonV5";
import type {AppState} from "../../../../data/state/AppState";
import type {View} from "../../../../data/models/View";
import type {Report} from "../../../../data/models/Report";
import {navBarZIndex} from "../../styles/styles";
import ShareIcon from "../../icons/share-dark.svg?react";
import TrashIcon from "../../icons/trash.svg?react";
import RenameIcon from "../../icons/pen-field.svg?react";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import type {Color} from "../../../../generated/api/base";
import DropletIcon from "../../icons/droplet.svg?react";
import {HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import {colorPalette} from "../../styles/colorPalette";
import {ViewColorIconSelectorV5} from "./ViewColorIconSelectorV5";
import {ViewElementStyled} from "./WorkspaceViewCommon";
import type {OnWorkspaceRenameActiveChange, WorkspaceViewType} from "./WorkspaceViewCommon";

export const ViewFolderClassName = "ViewFolderV5";

interface IViewFolderProps {
	readonly appState?: AppState;
	readonly onShareClick: (viewItem: View | IViewFolder | Report) => void;
	readonly data: IViewFolder;
	readonly saveViewFolderStructureToDatabase: () => void;
	readonly removeViewElementFromStructure: (viewElementId: string) => boolean;
	readonly onAddClick?: (viewFolderId: string) => void;
	readonly type: WorkspaceViewType;
	readonly searchString: string;
	readonly onViewPopupOpenChange: (isOpen: boolean) => void;
	readonly onWorkspaceRenameActiveChange: OnWorkspaceRenameActiveChange;
}

interface IViewFolderState {
	isInEditMode: boolean;
	isViewEllipsisOpen: boolean;
	isViewColorPaletteOpen: boolean;
	viewColor: Color;
}

@withAppStores("appState")
@observer
export class ViewFolderV5 extends React.Component<IViewFolderProps, IViewFolderState> {
	private _viewItemRef = React.createRef<HTMLDivElement>();
	private _dropdownButtonRef = React.createRef<HTMLDivElement>();
	private _originalColor: string | null = null;

	constructor(props: IViewFolderProps) {
		super(props);

		this.state = {
			isInEditMode: false,
			isViewEllipsisOpen: false,
			isViewColorPaletteOpen: false,
			viewColor: {
				hex: props.data.color || colorPalette.gray.c950,
				transparency: 0,
			},
		};
	}

	public override componentWillUnmount() {
		if (this.state.isViewColorPaletteOpen) {
			this.props.onViewPopupOpenChange(false);
		}

		if (this.state.isInEditMode) {
			this.props.onWorkspaceRenameActiveChange(false);
		}
	}

	private onToggleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
		const {saveViewFolderStructureToDatabase, data} = this.props;
		// Without this, the navigation panel can open up when you click on a view/folder in a popup
		e.stopPropagation();

		if (!this.state.isViewColorPaletteOpen && !this.state.isInEditMode) {
			data.isOpen = !data.isOpen;

			return saveViewFolderStructureToDatabase();
		}
	};

	private onDeleteClick = async () => {
		const {removeViewElementFromStructure, saveViewFolderStructureToDatabase, data} = this.props;
		const confirmed = await ConfirmWindowV5.open("Are you sure you want to delete the selected 1 item?");

		if (confirmed) {
			const isDeletionSuccessful = removeViewElementFromStructure(data.id);

			if (isDeletionSuccessful) {
				return saveViewFolderStructureToDatabase();
			}
		}
	};

	private onRenameClick = () => {
		// Workaround for focusloss/blur problem
		requestAnimationFrame(() => {
			if (!this.state.isInEditMode) {
				this.setState({
					isInEditMode: true,
				});
			}
		});
	};

	private onRenameApply = (value: string) => {
		const {saveViewFolderStructureToDatabase, data} = this.props;

		if (value !== data.name && value != "") {
			data.name = value;
			saveViewFolderStructureToDatabase();
		}

		this.onRenameBlur();
	};

	private onRenameBlur = () => {
		if (this.state.isInEditMode) {
			this.setState({
				isInEditMode: false,
			});
		}
	};

	private onAddClick = (event: React.MouseEvent) => {
		const {onAddClick, data} = this.props;
		event.stopPropagation();
		onAddClick?.(data.id);
	};

	private onShareClick = () => {
		const {onShareClick, data} = this.props;
		onShareClick(data);
	};

	private toggleViewIconColorPalette = () => {
		const isOpen = !this.state.isViewColorPaletteOpen;

		if (isOpen) {
			// Capture original color when opening
			this._originalColor = this.props.data.color || colorPalette.gray.c950;
		}

		this.setState({isViewColorPaletteOpen: isOpen});
		this.props.onViewPopupOpenChange(isOpen);
	};

	private onViewColorPaletteClose = () => {
		const {saveViewFolderStructureToDatabase, data, onViewPopupOpenChange} = this.props;
		const hasChanged = data.color !== this._originalColor;

		this._originalColor = null;
		this.setState({isViewColorPaletteOpen: false});
		onViewPopupOpenChange(false);

		if (hasChanged) {
			saveViewFolderStructureToDatabase();
		}
	};

	private onBackgroundColorChange = (newColor: Color) => {
		const {data} = this.props;
		this.setState({viewColor: newColor});
		data.color = newColor.hex;
	};

	public override componentDidUpdate(prevProps: IViewFolderProps, prevState: IViewFolderState) {
		if (prevState.isInEditMode !== this.state.isInEditMode) {
			this.props.onWorkspaceRenameActiveChange(this.state.isInEditMode);
		}
	}

	private getOptions = () => {
		const {type, appState, data} = this.props;
		const options: IDropdownOption[] = [];
		const isAdmin = appState.user?.isAdmin;

		if (type === "Favorites" || (isAdmin && type === "Global")) {
			options.push(
				{
					label: "Rename",
					onClick: this.onRenameClick,
					IconComponent: RenameIcon,
				},
				{
					label: "Color",
					onClick: this.toggleViewIconColorPalette,
					IconComponent: DropletIcon,
					iconCustomStyle: {
						strokeWidth: "2px",
					},
				},
			);
		}
		if (isAdmin && type != "Favorites") {
			options.push({
				label: "Share",
				onClick: this.onShareClick,
				IconComponent: ShareIcon,
			});
		}

		if (data.children?.length === 0 && (type === "Favorites" || (isAdmin && type === "Global"))) {
			options.push({
				label: "Delete",
				onClick: this.onDeleteClick,
				IconComponent: TrashIcon,
			});
		}

		if (options.length === 0) {
			return null;
		}

		return options;
	};

	public override render() {
		const {searchString, data, appState, onAddClick} = this.props;
		const {isViewEllipsisOpen, isInEditMode, isViewColorPaletteOpen, viewColor} = this.state;
		const {actions} = appState;
		const options = this.getOptions();
		const doesContainSearchString = StringUtils.containsIgnoreCase(data.name, searchString);
		const doesContainSearchedViewItem = doesStructureContainElement(data.children, searchString, actions);

		if (searchString && !doesContainSearchString && !doesContainSearchedViewItem) {
			return <></>;
		}

		const {isOpen, color} = data;
		const isOpenFinal = isOpen || (searchString && doesContainSearchedViewItem);

		return (
			<ViewElementStyled
				className={`${ViewFolderClassName} header hbox alignCenter flex_1`}
				onClick={this.onToggleOpen}
				$isActive={false}
				$isHovered={isViewEllipsisOpen || isViewColorPaletteOpen}
				ref={this._viewItemRef}
				$iconColor={color}
			>
				{isOpenFinal ? <OpenFolderIcon className="navIcon open" /> : <CloseFolderIcon className="navIcon close" />}
				{isInEditMode ? (
					<TextInputV5
						className="viewFolderName flex_1"
						value={data.name}
						onChange={this.onRenameApply}
						getErrorMessage={(value) => (value ? "" : "Folder name cannot be empty!")}
						onBlur={this.onRenameBlur}
						autoFocus={true}
					/>
				) : (
					<div className="viewFolderName flex_1">{data.name}</div>
				)}
				{onAddClick && (
					<IconButtonV5
						IconComponent={PlusIcon}
						onClick={this.onAddClick}
					/>
				)}
				{isViewColorPaletteOpen && (
					<ViewColorIconSelectorV5
						color={viewColor}
						onColorChange={this.onBackgroundColorChange}
						onCloseClick={this.onViewColorPaletteClose}
						isOpen={isViewColorPaletteOpen}
						elementDivRef={this._dropdownButtonRef}
						horizontalAlignment={HorizontalAlignment.outerRight}
						verticalAlignment={VerticalAlignment.bottom}
						offsetY={25}
						offsetX={-25}
						isMiniColorPalette={true}
					/>
				)}
				{options && (
					<div ref={this._dropdownButtonRef}>
						<DropdownButtonV5
							options={options}
							button={
								<IconButtonV5
									IconComponent={DotsHorizontalIcon}
									onClick={() => {
										if (isViewColorPaletteOpen) {
											this.onViewColorPaletteClose();
										}
										this.setState((prevState) => ({isViewEllipsisOpen: !prevState.isViewEllipsisOpen}));
									}}
								/>
							}
							optionsZIndex={navBarZIndex + 2}
							onOpenChange={(isOpen: boolean) => {
								if (!isOpen) {
									this.setState({isViewEllipsisOpen: false});
								}
							}}
							className="viewFolder"
						/>
					</div>
				)}
			</ViewElementStyled>
		);
	}
}
